/**
 * Option A RCA: client publisher + play-path (inbound) health via LiveKit data channel.
 * Worker mirrors data-channel packets into events.jsonl as client.*.
 */
import {
  Room,
  RoomEvent,
  Track,
  ConnectionQuality,
  type Participant,
  type TrackPublication,
} from 'livekit-client';
import { debugLog } from './debugLog';
import { isBotIdentity, isLiveAvatarIdentity } from './participantIdentity';
import { reportClientRca } from './reportClientRca';
import { installLocalMicDiagnostics } from './localMicDiagnostics';

function qualityLabel(q: ConnectionQuality | undefined): string | undefined {
  if (q == null) return undefined;
  return String(q);
}

async function publishRca(room: Room, payload: Record<string, unknown>): Promise<void> {
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    await room.localParticipant.publishData(bytes, { reliable: false });
  } catch {
    /* room may be closing */
  }
}

function reportHttp(sessionId: string, body: Record<string, unknown>): void {
  const type = String(body.type || '').replace(/^client\./, '');
  const meta = { ...body };
  delete meta.type;
  delete meta.message;
  reportClientRca(
    sessionId,
    type,
    meta,
    body.message != null ? String(body.message) : undefined,
  );
}

function sessionSnapshotMeta(room: Room, phase: string): Record<string, unknown> {
  let remoteAudioCount = 0;
  let remoteVideoCount = 0;
  room.remoteParticipants.forEach((p) => {
    p.audioTrackPublications.forEach((pub) => {
      if (pub.track) remoteAudioCount += 1;
    });
    p.videoTrackPublications.forEach((pub) => {
      if (pub.track) remoteVideoCount += 1;
    });
  });
  return {
    phase,
    remoteParticipantCount: room.remoteParticipants.size,
    remoteAudioCount,
    remoteVideoCount,
    can_playback: room.canPlaybackAudio ? 1 : 0,
    state: String(room.state || ''),
  };
}

function findPublisherPc(room: Room): RTCPeerConnection | null {
  const engine = (room as unknown as { engine?: Record<string, unknown> }).engine;
  if (!engine) return null;
  const pcManager = engine.pcManager as
    | { publisher?: { pc?: RTCPeerConnection }; subscriber?: { pc?: RTCPeerConnection } }
    | undefined;
  if (pcManager?.publisher?.pc) return pcManager.publisher.pc;
  const publisher = engine.publisher as { pc?: RTCPeerConnection } | undefined;
  return publisher?.pc ?? null;
}

function findSubscriberPc(room: Room): RTCPeerConnection | null {
  const engine = (room as unknown as { engine?: Record<string, unknown> }).engine;
  if (!engine) return null;
  const pcManager = engine.pcManager as
    | { subscriber?: { pc?: RTCPeerConnection } }
    | undefined;
  if (pcManager?.subscriber?.pc) return pcManager.subscriber.pc;
  const subscriber = engine.subscriber as { pc?: RTCPeerConnection } | undefined;
  return subscriber?.pc ?? null;
}

function classifyPlayPath(identity: string | undefined): 'liveavatar' | 'bot' | 'other' {
  if (isLiveAvatarIdentity(identity)) return 'liveavatar';
  if (isBotIdentity(identity)) return 'bot';
  return 'other';
}

type PlayPathRow = {
  play_path: 'liveavatar' | 'bot' | 'other';
  play_identity: string;
  play_packets_received: number;
  play_packets_lost: number;
  play_jitter: number;
  play_nack: number;
  play_concealed_samples: number;
  play_silent_concealed_samples: number;
  play_inserted_samples: number;
  play_removed_samples: number;
};

function pickPrimaryPlayPath(rows: PlayPathRow[]): PlayPathRow | null {
  if (!rows.length) return null;
  return (
    rows.find((r) => r.play_path === 'liveavatar') ||
    rows.find((r) => r.play_path === 'bot') ||
    rows[0] ||
    null
  );
}

/**
 * Inbound RTP for remote audio the client may play (LA / bot).
 * Uses subscriber PeerConnection receivers — not publisher uplink.
 */
async function collectInboundPlayStats(
  room: Room,
): Promise<Record<string, string | number>> {
  const pc = findSubscriberPc(room);
  if (!pc || typeof pc.getReceivers !== 'function') {
    return {};
  }

  const trackToIdentity = new Map<string, string>();
  room.remoteParticipants.forEach((p) => {
    p.audioTrackPublications.forEach((pub) => {
      const track = pub.track;
      const mst = track?.mediaStreamTrack;
      if (mst?.id) trackToIdentity.set(mst.id, p.identity || '');
    });
  });

  const byIdentity = new Map<string, PlayPathRow>();

  for (const receiver of pc.getReceivers()) {
    const track = receiver.track;
    if (!track || track.kind !== 'audio') continue;
    if (typeof receiver.getStats !== 'function') continue;
    let report: RTCStatsReport;
    try {
      report = await receiver.getStats();
    } catch {
      continue;
    }
    const identity = trackToIdentity.get(track.id) || '';
    const path = classifyPlayPath(identity || undefined);
    let row = byIdentity.get(identity || path);
    if (!row) {
      row = {
        play_path: path,
        play_identity: identity || path,
        play_packets_received: 0,
        play_packets_lost: 0,
        play_jitter: 0,
        play_nack: 0,
        play_concealed_samples: 0,
        play_silent_concealed_samples: 0,
        play_inserted_samples: 0,
        play_removed_samples: 0,
      };
      byIdentity.set(identity || path, row);
    }
    report.forEach((s) => {
      if (s.type !== 'inbound-rtp') return;
      const i = s as RTCInboundRtpStreamStats & {
        kind?: string;
        mediaType?: string;
        nackCount?: number;
        concealedSamples?: number;
        silentConcealedSamples?: number;
        insertedSamplesForDeceleration?: number;
        removedSamplesForAcceleration?: number;
      };
      const kind = i.kind || i.mediaType;
      if (kind && kind !== 'audio') return;
      row!.play_packets_received += i.packetsReceived || 0;
      row!.play_packets_lost += i.packetsLost || 0;
      if (i.jitter != null) row!.play_jitter = i.jitter;
      row!.play_nack += i.nackCount || 0;
      row!.play_concealed_samples += i.concealedSamples || 0;
      row!.play_silent_concealed_samples += i.silentConcealedSamples || 0;
      row!.play_inserted_samples += i.insertedSamplesForDeceleration || 0;
      row!.play_removed_samples += i.removedSamplesForAcceleration || 0;
    });
  }

  const rows = [...byIdentity.values()];
  const primary = pickPrimaryPlayPath(rows);
  if (!primary) return { play_paths: 0 };
  return {
    ...primary,
    play_paths: rows.length,
  };
}

/** Uplink audio counters only — for mic RCA correlation. */
export async function collectPublisherOutboundAudio(
  room: Room,
): Promise<{ audio_packets_sent: number; audio_bytes_sent: number }> {
  const pc = findPublisherPc(room);
  let audioPackets = 0;
  let audioBytes = 0;
  if (!pc || typeof pc.getStats !== 'function') {
    return { audio_packets_sent: audioPackets, audio_bytes_sent: audioBytes };
  }
  const report = await pc.getStats();
  report.forEach((s) => {
    if (s.type !== 'outbound-rtp') return;
    const o = s as RTCOutboundRtpStreamStats & { kind?: string; mediaType?: string };
    const kind = o.kind || o.mediaType;
    if (kind === 'audio') {
      audioPackets += o.packetsSent || 0;
      audioBytes += o.bytesSent || 0;
    }
  });
  return { audio_packets_sent: audioPackets, audio_bytes_sent: audioBytes };
}

async function collectOutboundStats(room: Room): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {
    type: 'client.webrtc_stats',
    connection_quality: qualityLabel(room.localParticipant.connectionQuality),
  };
  const pc = findPublisherPc(room);
  if (!pc || typeof pc.getStats !== 'function') {
    Object.assign(out, await collectInboundPlayStats(room));
    return out;
  }

  const report = await pc.getStats();
  const remoteInbound = new Map<number, RTCStats>();
  report.forEach((s) => {
    if (s.type === 'remote-inbound-rtp') {
      const ssrc = Number((s as { ssrc?: number }).ssrc || 0);
      remoteInbound.set(ssrc, s);
    }
  });

  let audioPackets = 0;
  let audioBytes = 0;
  let audioLost = 0;
  let audioJitter = 0;
  let audioNack = 0;
  let audioRtt: number | undefined;
  let videoPackets = 0;
  let videoBytes = 0;

  report.forEach((s) => {
    if (s.type !== 'outbound-rtp') return;
    const o = s as RTCOutboundRtpStreamStats & { kind?: string; mediaType?: string; nackCount?: number };
    const kind = o.kind || o.mediaType;
    if (kind === 'audio') {
      audioPackets += o.packetsSent || 0;
      audioBytes += o.bytesSent || 0;
      audioNack += o.nackCount || 0;
      const ri = remoteInbound.get(Number(o.ssrc || 0)) as
        | (RTCStats & { packetsLost?: number; jitter?: number; roundTripTime?: number })
        | undefined;
      if (ri) {
        audioLost += ri.packetsLost || 0;
        if (ri.jitter != null) audioJitter = ri.jitter;
        if (ri.roundTripTime != null) audioRtt = Math.round(ri.roundTripTime * 1000);
      }
    } else if (kind === 'video') {
      videoPackets += o.packetsSent || 0;
      videoBytes += o.bytesSent || 0;
    }
  });

  out.audio_packets_sent = audioPackets;
  out.audio_bytes_sent = audioBytes;
  out.audio_packets_lost = audioLost;
  out.audio_jitter = audioJitter;
  out.audio_nack = audioNack;
  if (audioRtt != null) out.audio_rtt_ms = audioRtt;
  out.video_packets_sent = videoPackets;
  out.video_bytes_sent = videoBytes;

  Object.assign(out, await collectInboundPlayStats(room));
  return out;
}

/** Install mute / visibility / 2s webrtc stats RCA. Returns disposer. */
export function installClientRca(room: Room, sessionId: string): () => void {
  const intervalIds: number[] = [];
  const timeoutIds: number[] = [];
  const cleanups: Array<() => void> = [];

  reportHttp(sessionId, {
    type: 'client.session_snapshot',
    ...sessionSnapshotMeta(room, 'start'),
  });

  const onVis = () => {
    const state = document.visibilityState;
    const payload = { type: 'client.visibility', state };
    void publishRca(room, payload);
    reportHttp(sessionId, payload);
    debugLog('client.visibility', { state });
  };
  document.addEventListener('visibilitychange', onVis);
  cleanups.push(() => document.removeEventListener('visibilitychange', onVis));

  timeoutIds.push(
    window.setTimeout(() => {
      const payload = {
        type: 'client.audio_context',
        can_playback: room.canPlaybackAudio ? 1 : 0,
      };
      void publishRca(room, payload);
      reportHttp(sessionId, payload);
    }, 1500),
  );

  const onMute = (pub: TrackPublication, participant?: Participant) => {
    if (participant && !participant.isLocal) return;
    if (pub.kind !== Track.Kind.Audio && pub.source !== Track.Source.Microphone) return;
    const payload = {
      type: 'client.track_muted',
      source: String(pub.source),
      track_sid: pub.trackSid,
    };
    void publishRca(room, payload);
    reportHttp(sessionId, payload);
  };
  const onUnmute = (pub: TrackPublication, participant?: Participant) => {
    if (participant && !participant.isLocal) return;
    if (pub.kind !== Track.Kind.Audio && pub.source !== Track.Source.Microphone) return;
    const payload = {
      type: 'client.track_unmuted',
      source: String(pub.source),
      track_sid: pub.trackSid,
    };
    void publishRca(room, payload);
    reportHttp(sessionId, payload);
  };
  room.on(RoomEvent.TrackMuted, onMute as (...args: unknown[]) => void);
  room.on(RoomEvent.TrackUnmuted, onUnmute as (...args: unknown[]) => void);
  cleanups.push(() => {
    room.off(RoomEvent.TrackMuted, onMute as (...args: unknown[]) => void);
    room.off(RoomEvent.TrackUnmuted, onUnmute as (...args: unknown[]) => void);
  });

  const onQuality = (quality: ConnectionQuality, participant?: Participant) => {
    if (participant && !participant.isLocal) return;
    const q = qualityLabel(quality ?? room.localParticipant.connectionQuality);
    // Data-channel only — HTTP quality spam would burn durable/ephemeral caps.
    void publishRca(room, {
      type: 'client.webrtc_stats',
      connection_quality: q,
      quality_only: true,
    });
  };
  room.on(RoomEvent.ConnectionQualityChanged, onQuality as (...args: unknown[]) => void);
  cleanups.push(() =>
    room.off(RoomEvent.ConnectionQualityChanged, onQuality as (...args: unknown[]) => void),
  );

  intervalIds.push(
    window.setInterval(() => {
      void collectOutboundStats(room)
        .then((payload) => publishRca(room, { type: 'client.webrtc_stats', ...payload }))
        .catch(() => undefined);
    }, 2000),
  );

  // Sparse HTTP (~10s) ephemeral webrtc_stats + durable play_path_summary every ~30s.
  let httpTick = 0;
  intervalIds.push(
    window.setInterval(() => {
      httpTick += 1;
      if (httpTick % 5 !== 0) return;
      void collectOutboundStats(room)
        .then((payload) => {
          // Ephemeral list (removed on hangup) — still useful mid-call.
          reportHttp(sessionId, { type: 'client.webrtc_stats', ...payload });
          if (httpTick % 15 === 0) {
            reportHttp(sessionId, { type: 'client.play_path_summary', ...payload });
          }
        })
        .catch(() => undefined);
    }, 2000),
  );

  const stopMicDiag = installLocalMicDiagnostics(room, sessionId);
  cleanups.push(stopMicDiag);

  return () => {
    void collectOutboundStats(room)
      .then((payload) => {
        reportHttp(sessionId, {
          type: 'client.session_snapshot',
          ...sessionSnapshotMeta(room, 'end'),
          ...payload,
        });
        reportHttp(sessionId, { type: 'client.play_path_summary', phase: 'end', ...payload });
      })
      .catch(() => {
        reportHttp(sessionId, {
          type: 'client.session_snapshot',
          ...sessionSnapshotMeta(room, 'end'),
        });
      });
    intervalIds.forEach((id) => window.clearInterval(id));
    timeoutIds.forEach((id) => window.clearTimeout(id));
    cleanups.forEach((fn) => fn());
  };
}
